import React, { useEffect, useRef, useState } from "react";
import OnboardingVideo from "./OnboardingVideo";
import DemoMobilePreview from "../DemoMobilePreview";
import {
  ONBOARDING_INDUSTRIES,
  READY_MESSAGE_BY_INDUSTRY,
  PREPARING_LINES,
  WELCOME_VIDEO,
} from "./onboarding.config";
import { trackSuiteEvent } from "shared/util/track";
import "./DemoOnboarding.css";

const STEPS = ["welcome", "industry", "preparing", "ready"];
const PREPARING_MS = 2600;

/**
 * Prop-driven onboarding overlay. Demo'ya özel değildir; sadece şu callback'leri bilir:
 *  - onComplete(industry): kullanıcı akışı bitirip demoyu görmek istedi
 *  - track(name, type, meta): opsiyonel; default Suite Events (isInternationalTheme gate'li)
 *
 * Görünürlük/flag yönetimi DIŞARIDA (provider) yapılır → bileşen yeniden kullanılabilir
 * (ör. ileride signup onboarding'i için farklı config/callback ile).
 */
const DemoOnboarding = ({ show, onComplete, onIndustrySelect, track = trackSuiteEvent }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [industry, setIndustry] = useState(null);
  const startedRef = useRef(false);
  const videoStartedRef = useRef(false);
  const step = STEPS[stepIndex];

  // onboarding_started — bir kez
  useEffect(() => {
    if (show && !startedRef.current) {
      startedRef.current = true;
      track("Onboarding — Started", "modal_view", { key: "onboarding_started" });
    }
  }, [show, track]);

  // "Hazırlanıyor" → otomatik ilerle (+ personalized_demo_created)
  useEffect(() => {
    if (step !== "preparing") return undefined;
    track("Onboarding — Personalized Demo Created", "other", {
      key: "personalized_demo_created",
      industry: industry || "both",
    });
    const tmr = setTimeout(() => setStepIndex((i) => i + 1), PREPARING_MS);
    return () => clearTimeout(tmr);
  }, [step, industry, track]);

  if (!show) return null;

  const handleVideoStarted = () => {
    if (videoStartedRef.current) return;
    videoStartedRef.current = true;
    track("Onboarding — Welcome Video Started", "other", { key: "welcome_video_started" });
  };

  const handleStart = () => {
    track("Onboarding — Welcome Started", "button_click", { key: "welcome_continued" });
    setStepIndex(1);
  };

  const handleIndustry = (value) => {
    setIndustry(value);
    track("Onboarding — Industry Selected", "button_click", { key: "industry_selected", industry: value });
    // Seçime göre company domain/permission'larını güncelle (provider yapar).
    if (onIndustrySelect) onIndustrySelect(value);
    setStepIndex(2);
  };

  const handleFinish = () => {
    track("Onboarding — Completed", "button_click", { key: "onboarding_completed", industry: industry || "both" });
    if (onComplete) onComplete(industry);
  };

  const progressIndex = step === "welcome" ? 0 : step === "industry" ? 1 : 2;
  const canSkipVideo = step === "welcome";

  return (
    <div className="ob-overlay" role="dialog" aria-modal="true" aria-label="RouteBot demo setup">
      <div className={`ob-card${step === "welcome" ? " ob-card--welcome" : ""}`} key={step}>
        {canSkipVideo && (
          <button type="button" className="ob-skip" onClick={handleStart}>
            Skip video →
          </button>
        )}

        {step !== "preparing" && (
          <div className="ob-progress" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <span key={i} className={`ob-dot${i === progressIndex ? " ob-dot--active" : ""}${i < progressIndex ? " ob-dot--done" : ""}`} />
            ))}
          </div>
        )}

        {/* ── WELCOME ── */}
        {step === "welcome" && (
          <div className="ob-step ob-step--welcome">
            <OnboardingVideo video={WELCOME_VIDEO} onStarted={handleVideoStarted} autoPlay />
            <span className="ob-badge">From the founder</span>
            <h2 className="ob-title">Hi — I&apos;m Emrah, founder of RouteBot</h2>
            <p className="ob-subtitle">
              This is the real product: the dispatcher dashboard you&apos;re about to open, plus the
              driver and parent apps on the same routes. Watch a minute, or jump in.
            </p>
            <div className="ob-actions">
              <button type="button" className="ob-btn ob-btn--primary" onClick={handleStart}>
                Continue
              </button>
            </div>
          </div>
        )}

        {/* ── INDUSTRY ── */}
        {step === "industry" && (
          <div className="ob-step">
            <h2 className="ob-title">What do you run?</h2>
            <p className="ob-subtitle">So we can show you the right setup.</p>
            <div className="ob-industry-grid">
              {ONBOARDING_INDUSTRIES.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className="ob-industry-card"
                  onClick={() => handleIndustry(opt.value)}
                >
                  <span className="ob-industry-emoji" aria-hidden="true">
                    {opt.emoji}
                  </span>
                  <span className="ob-industry-label">{opt.label}</span>
                  <span className="ob-industry-desc">{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── PREPARING ── */}
        {step === "preparing" && (
          <div className="ob-step ob-step--preparing">
            <div className="ob-spinner" aria-hidden="true" />
            <h2 className="ob-title">Setting up your demo…</h2>
            <ul className="ob-prep-lines">
              {PREPARING_LINES.map((line, i) => (
                <li key={line} className="ob-prep-line" style={{ animationDelay: `${i * 0.6}s` }}>
                  {line}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ── READY ── */}
        {step === "ready" && (
          <div className="ob-step ob-step--ready">
            <div className="ob-check" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="34" height="34">
                <path
                  d="M20 6L9 17l-5-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="ob-title">Dashboard + the apps</h2>
            <p className="ob-subtitle">
              {READY_MESSAGE_BY_INDUSTRY[industry] || READY_MESSAGE_BY_INDUSTRY.default} You&apos;ll
              tour the dispatcher dashboard next. Drivers and parents see the same routes on their
              phones.
            </p>
            <DemoMobilePreview />
            <div className="ob-actions">
              <button type="button" className="ob-btn ob-btn--primary" onClick={handleFinish}>
                Open the dashboard →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoOnboarding;
